// The composer pill's motion at the panel's foot. The pill is laid out against
// the panel's bottom edge and its spacer, which jump when the layout viewport
// resizes for the keyboard (Android, and an iOS shell that resizes its
// WebView) or when the reserve and the navbar's clearance change — and they
// do so on the platform's clock: after the keyboard's slide on Chrome, with
// it under the shells, at its end where the keyboard overlays the page. Left
// to the layout, the pill jumped with them: it vanished under a rising
// keyboard and popped out at the top of it, or waited under it.
//
// So the pill glides. While a keyboard is coming, up or going, a jump of the
// pill's layout place is taken as a motion: at the jump the pill holds where
// it was on screen (a transform undoing the jump) and glides to its new place
// over about the keyboard's own slide. A change known to be coming is glided
// toward ahead of it: at focus, the remembered keyboard's height lifts the
// pill before the viewport or the reserve has moved it, and the move is
// absorbed into the lift when it lands; as the keyboard leaves, the way back
// to the foot's resting place is glided ahead of the viewport growing. And a
// change that is itself animated — the shell resizing the WebView frame by
// frame with the keyboard's curve — is ridden, not held: each frame's step is
// too small to be a jump, and the layout is the motion.
export const BOTTOM_EDITOR_GLIDE_MS = 250
export const BOTTOM_EDITOR_GLIDE_EASING = 'cubic-bezier(0.2, 0, 0, 1)'
// A change of the pill's place at least this big is a jump to be held and
// glided; smaller steps are an animation in progress, and are ridden.
export const BOTTOM_EDITOR_GLIDE_JUMP_PX = 48

// What the keyboard is doing, as the panel foot sees it: expected (focus has
// landed, the keyboard not yet reported), open, leaving (focus has left or
// the keyboard has just gone and the foot is on its way back), or none.
export type KeyboardPhase = 'none' | 'expecting' | 'open' | 'leaving'

// Where the foot is: the host's bottom edge on screen, and the spacer between
// that edge and the pill (the navbar's clearance, the keyboard's reserve).
// Neither depends on the pill's own height, which folds and grows on its own
// motion (NewAnswer.animateCompactComposerResize).
export type GlideFrame = { hostBottom: number; spacer: number }

export type GlideState = {
    // The frame last measured.
    frame: GlideFrame | null
    // The frame at rest — no keyboard coming, up or going — for the way back.
    rest: GlideFrame | null
    // A shift of the pill's place still to come, in transform terms: negative
    // for a lift the layout has yet to make, positive for a drop.
    pending: number
}

export type GlideStep = {
    state: GlideState
    // A transform to apply at once, without transition, so the pill stays
    // where it was on screen through a jump; null when nothing jumped.
    hold: number | null
    // The transform to glide to.
    rest: number
}

export function initialGlideState(): GlideState {
    return { frame: null, rest: null, pending: 0 }
}

// The pill's bottom edge on screen, from a frame.
function placeOf(frame: GlideFrame): number {
    return frame.hostBottom - frame.spacer
}

function noNegativeZero(value: number): number {
    return value === 0 ? 0 : value
}

export function resolveGlide({
    state,
    frame,
    translateY,
    phase,
    rememberedKeyboardHeight = 0,
}: {
    state: GlideState
    frame: GlideFrame
    // The pill's current transform, as it stands mid-glide.
    translateY: number
    phase: KeyboardPhase
    // The last keyboard's height: where the pill heads while one is expected.
    rememberedKeyboardHeight?: number
}): GlideStep {
    if (phase === 'none') {
        return { state: { frame, rest: frame, pending: 0 }, hold: null, rest: 0 }
    }
    const rest = state.rest ?? state.frame ?? frame
    const previous = state.frame
    // How far the pill's place moved on screen since the last frame (down is
    // positive), and whether that is a jump or a step of an animation.
    const placeShift = previous ? placeOf(frame) - placeOf(previous) : 0
    const jumped = Math.abs(placeShift) >= BOTTOM_EDITOR_GLIDE_JUMP_PX
    const moved = Math.abs(placeShift) >= 1

    let pending = state.pending
    // The layout moving the way it was expected to is the expected shift
    // arriving: whatever its exact size, the layout is now the truth.
    if (pending !== 0 && moved && Math.sign(placeShift) === Math.sign(pending)) pending = 0

    if (phase === 'expecting') {
        // The keyboard will lift the pill by about its height, less whatever
        // of that lift the layout has already made since rest.
        const liftSoFar = Math.max(0, placeOf(rest) - placeOf(frame))
        const remaining = Math.max(0, Math.max(0, rememberedKeyboardHeight) - liftSoFar)
        pending = remaining === 0 ? 0 : -remaining
    } else if (phase === 'leaving') {
        // The way back to rest, whatever of it the layout has yet to make.
        pending = noNegativeZero(placeOf(rest) - placeOf(frame))
    }

    const hold = jumped ? noNegativeZero(translateY - placeShift) : null
    return { state: { frame, rest, pending }, hold, rest: pending }
}

// Put a step on the glide element: the hold at once, then the rest as a
// transition. Setting the same rest again mid-glide does not restart it.
export function applyGlideStep(
    element: HTMLElement,
    step: GlideStep,
    { reduceMotion = false }: { reduceMotion?: boolean } = {},
): void {
    const transformFor = (translateY: number) =>
        Math.abs(translateY) < 0.5 ? '' : `translateY(${translateY}px)`
    if (step.hold != null && !reduceMotion) {
        element.style.transition = 'none'
        element.style.transform = transformFor(step.hold)
        // Commit the hold before the transition, or the two collapse into one
        // style change and the pill jumps after all.
        void element.offsetHeight
    }
    element.style.transition = reduceMotion
        ? 'none'
        : `transform ${BOTTOM_EDITOR_GLIDE_MS}ms ${BOTTOM_EDITOR_GLIDE_EASING}`
    element.style.transform = transformFor(step.rest)
}

// The foot's keyboard phase from the panel controller's keyboard state.
export function resolveKeyboardPhase({
    keyboardOpen,
    keyboardExpected,
    keyboardLeaving,
}: {
    keyboardOpen: boolean
    keyboardExpected: boolean
    // Focus has left the keyboard's field, or the keyboard has just gone and
    // the foot is settling back.
    keyboardLeaving: boolean
}): KeyboardPhase {
    if (keyboardOpen) return 'open'
    if (keyboardExpected) return 'expecting'
    if (keyboardLeaving) return 'leaving'
    return 'none'
}
